<script>
	import { createEventDispatcher } from 'svelte';

	export let isLoading = true;
	export let categories = [];
	export let activeCategory = null;
	export let activeView = 'all';
	export let amplifiedProducts = [];

	let categorySearchQuery = '';

	const dispatch = createEventDispatcher();

	function selectView(view) {
		dispatch('selectView', view);
	}

	function selectCategory(categoryName) {
		dispatch('selectCategory', categoryName);
	}

	$: filteredCategories = categorySearchQuery
		? categories.filter((category) =>
				category.name.toLowerCase().includes(categorySearchQuery.toLowerCase())
		  )
		: categories;
</script>

<aside class="sidebar">
	<div class="sidebar-section views-section">
		<h2 class="sidebar-heading">Views</h2>
		<ul class="sidebar-list">
			<li>
				<button
					class="sidebar-button {activeView === 'all' ? 'is-active' : ''}"
					on:click={() => selectView('all')}
				>
					All Products
				</button>
			</li>
			<li>
				<button
					class="sidebar-button {activeView === 'amplified' ? 'is-active' : ''}"
					on:click={() => selectView('amplified')}
				>
					Amplified Products
					<span class="amplified-count"> {amplifiedProducts.length} </span>
				</button>
			</li>
			<li>
				<button
					class="sidebar-button {activeView === 'on_sale' ? 'is-active' : ''}"
					on:click={() => selectView('on_sale')}
				>
					On Sale
				</button>
			</li>
		</ul>
	</div>

	<div class="sidebar-section categories-section">
		<h2 class="sidebar-heading">Categories</h2>

		<div class="category-search-container">
			<input
				type="text"
				placeholder="Filter categories..."
				class="category-search-input"
				bind:value={categorySearchQuery}
			/>
		</div>

		{#if isLoading && categories.length === 0}
			<div class="loading-placeholder">
				{#each Array(5) as _}
					<div class="placeholder-item" />
				{/each}
			</div>
		{:else if categories.length === 0}
			<p class="no-items-message">No categories found</p>
		{:else if filteredCategories.length === 0}
			<p class="no-items-message">No matching categories found</p>
		{:else}
			<div class="category-list-scroll">
				<ul class="sidebar-list">
					{#each filteredCategories as category}
						<li>
							<button
								class="sidebar-button category-button {activeCategory === category.name
									? 'is-active'
									: ''}"
								on:click={() => selectCategory(category.name)}
							>
								<span class="category-name">{category.name}</span>
								<span class="category-count"> {category.count} </span>
							</button>
						</li>
					{/each}
				</ul>
			</div>
		{/if}
	</div>
</aside>

<style>
	.sidebar {
		width: 100%;
		flex-shrink: 0;
		position: sticky;
		top: calc(1em + 50px);
	}

	@media (min-width: 1024px) {
		.sidebar {
			width: 16rem;
		}
	}

	.sidebar-section {
		margin-bottom: 1rem;
		border-radius: 0.5rem;
		background-color: var(--white);
		padding: 1rem;
		box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
	}

	.sidebar-heading {
		margin-bottom: 0.75rem;
		font-size: 1.125rem;
		line-height: 1.75rem;
		font-weight: 600;
	}

	.sidebar-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.sidebar-button {
		width: 100%;
		border-radius: 0.375rem;
		padding: 0.5rem 0.75rem;
		text-align: left;
		background: none;
		border: none;
		cursor: pointer;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--grey-600);
		transition: background-color 0.15s ease-in-out;
	}
	.sidebar-button:hover {
		background-color: var(--grey-200);
	}
	.sidebar-button.is-active {
		background-color: var(--grey-200);
		color: var(--grey-600);
	}

	.category-button {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.category-name {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.amplified-count,
	.category-count {
		margin-left: 0.5rem;
		border-radius: 9999px;
		padding: 0.125rem 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
	}

	.amplified-count {
		background-color: var(--grey-200);
		color: var(--grey-600);
	}

	.category-count {
		background-color: var(--grey-200);
		color: var(--grey-600);
	}

	.category-search-container {
		margin-bottom: 0.75rem;
	}

	.category-search-input {
		width: 100%;
		border-radius: 0.375rem;
		border: 1px solid var(--grey-200);
		padding: 0.5rem 0.75rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		outline: none;
		transition: box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out;
	}
	.category-search-input:focus {
		border-color: var(--accent);
		box-shadow: var(--shadow-focus-sidebar);
	}

	.loading-placeholder {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}
	.placeholder-item {
		height: 2.5rem;
		border-radius: 0.25rem;
		background-color: var(--grey-200);
	}

	.no-items-message {
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--grey-600);
	}

	.category-list-scroll {
		max-height: 20rem;
		overflow-y: auto;
		padding-right: 0.5rem;
	}

	.category-list-scroll::-webkit-scrollbar {
		width: 6px;
	}
	.category-list-scroll::-webkit-scrollbar-track {
		background: var(--grey-200);
		border-radius: 3px;
	}
	.category-list-scroll::-webkit-scrollbar-thumb {
		background: var(--grey-200);
		border-radius: 3px;
	}
	.category-list-scroll::-webkit-scrollbar-thumb:hover {
		background: var(--grey-200);
	}
</style>
